import { Formik, Field, Form, ErrorMessage } from 'formik';
import { useEffect, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import Logo from './../images/Logo.webp';
import * as Yup from 'yup';
import '../css/login.css'
import axios from 'axios';
import { useNavigate } from "react-router-dom";
import { useDispatch } from 'react-redux';
import { setToken } from '../stores/userSlice';
import AuthIllustration from '../components/AuthIllustration';

function Login (){
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [showPassword, setShowPassword] = useState(false);
    const [loginError, setLoginError] = useState("");
    const [requires2FA, setRequires2FA] = useState(false);
    const [tempToken, setTempToken] = useState("");
    const [twoFactorCode, setTwoFactorCode] = useState("");
    const [isSubmitting2FA, setIsSubmitting2FA] = useState(false);
    const [loginUsername, setLoginUsername] = useState("");
    const Kaitongo_Mail = process.env.REACT_APP_KAITONGO_MANAGER_EMAILS.split(',');
    const initialValues = {
        username:'',
        password: ''
    };
    const validationSchema =Yup.object().shape({       
        username: Yup.string()
            .email("*Field should contain a valid data-aces e-mail")
            .required('*User name is required'),
        password: Yup.string()
            .required('*Password is required'),
    });

    const validatUser = async (values, { setSubmitting }) => { 
        setSubmitting(true);
        setLoginError("");
        if ((values.username.indexOf("@data-aces") !== -1 || values.username.indexOf("@kaitongo")!== -1)  && values.password) {
            try{
                const {data} = await axios.post(`${process.env.REACT_APP_BASE_URL}/auth`, {
                    username: values.username,
                    password: values.password,
                })
                setSubmitting(false);
                
                if (data?.requires2FA) {
                    setRequires2FA(true);
                    setTempToken(data.tempToken);
                    setLoginUsername(values.username);
                    return;
                }
                
                dispatch(setToken({ token: data?.token, refreshToken: data?.refreshToken }));
                if(Kaitongo_Mail.includes(values.username)){
                    navigate("/admin");  
                }else{
                    if(data?.role == "Admin"){
                        navigate("/dashboard");
                    }else if(data?.role == "Manager"){
                        navigate("/admin");
                    }else if(data?.role == "HR"){
                        navigate("/hr-panel");
                    }else{
                        navigate("/dashboard");  
                    }
                }
            }catch(error){
                setSubmitting(false);
                console.log(error)
                setLoginError("Login failed. Please check your user credentials.");
            }
        } else {
            setSubmitting(false);
            setLoginError("Please enter valid inputs.");
        }            
    }

    const handle2FASubmit = async (e) => {
        e.preventDefault();
        setLoginError("");
        if (!twoFactorCode || (twoFactorCode.length !== 6 && twoFactorCode.length !== 8)) {
            setLoginError("Please enter a valid 6-digit code or 8-character recovery code.");
            return;
        }
        setIsSubmitting2FA(true);
        try {
            const { data } = await axios.post(`${process.env.REACT_APP_BASE_URL}/auth/2fa`, {
                tempToken,
                code: twoFactorCode
            });
            dispatch(setToken({ token: data?.token, refreshToken: data?.refreshToken }));
            if(Kaitongo_Mail.includes(loginUsername)){
                navigate("/admin");  
            } else {
                if(data?.role == "Admin") navigate("/dashboard");
                else if(data?.role == "Manager") navigate("/admin");
                else if(data?.role == "HR") navigate("/hr-panel");
                else navigate("/dashboard");  
            }
        } catch(error) {
            console.log(error);
            const data = error?.response?.data;
            if (data?.sessionExpired || error?.response?.status === 401) {
                setRequires2FA(false);
                setTempToken('');
                setTwoFactorCode('');
                setLoginError(
                    data?.message || "Your Two-Factor Authentication session has expired. Please log in again."
                );
            } else {
                setLoginError(data?.message || "Invalid or expired 2FA code.");
                setTwoFactorCode('');
            }
        } finally {
            setIsSubmitting2FA(false);
        }
    }

    return (
        <div className="relative flex min-h-screen w-full items-center justify-center overflow-hidden px-4 py-6 sm:px-6 lg:px-8">
            <div className="relative z-10 mx-auto grid w-full max-w-6xl gap-8 xl:gap-16 lg:grid-cols-2 lg:items-center">
                {/* Left side text */}
                <div className="hidden lg:flex flex-col lg:pl-10 xl:pl-16 max-w-lg">
                    <div className="flex items-center justify-start w-full mb-8">
                        <img src={Logo} alt="Data Aces Logo" className="h-10" />
                    </div>
                    
                    <h1 className="text-4xl font-bold text-slate-800 mb-2">Welcome back!</h1>
                    <h2 className="text-3xl font-bold text-orange-500 mb-4 flex items-center gap-2">Glad to see you again 👋</h2>
                    <div className="w-12 h-1 bg-orange-500 mb-6 rounded-full"></div>
                    
                    <p className="text-slate-500 mb-8 text-base">
                        Sign in to your Data Aces account<br/>
                        to continue managing your workforce.
                    </p>
                </div>

                {/* Right side form */}
                <div className="self-center bg-white rounded-2xl border border-slate-200 shadow-sm p-6 md:p-8 w-full max-w-md mx-auto relative max-h-[85vh] overflow-y-auto custom-scrollbar">
                    <div className="flex justify-center mb-4 lg:hidden">
                        <img src={Logo} alt="Data Aces Logo" className="h-10" />
                    </div>
                    <h2 className="text-2xl font-bold text-slate-800 mb-1">Sign in</h2>
                    <p className="text-slate-500 mb-4 text-sm">Enter your login credentials below</p>

                    {requires2FA ? (
                        <form className="space-y-4" onSubmit={handle2FASubmit}>
                            <div>
                                <label className="block text-sm font-semibold text-slate-800 mb-1">Authentication Code</label>
                                <p className="text-xs text-slate-500 mb-2">Enter the 6-digit code from your Authenticator app, or an 8-character recovery code.</p>
                                <input 
                                    autoFocus 
                                    type="text" 
                                    maxLength={10} 
                                    value={twoFactorCode} 
                                    onChange={e => {
                                        setTwoFactorCode(e.target.value.replace(/\s/g, "").slice(0, 8));
                                        if (loginError) setLoginError('');
                                    }} 
                                    placeholder="000000" 
                                    className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 hover:border-blue-500 transition-colors text-center tracking-widest text-lg font-bold uppercase" 
                                />
                            </div>
                            {loginError && (
                                <div className="px-3 py-2 rounded-md bg-red-50 text-red-600 text-xs font-medium flex gap-1.5 border border-red-100">
                                    <svg className='mt-0.5' xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                                    {loginError}
                                </div>
                            )}
                            <button type="submit" disabled={isSubmitting2FA} className="w-full flex items-center justify-center gap-2 py-2.5 bg-[#2563eb] text-white rounded-xl hover:bg-blue-700 transition-colors font-semibold text-sm shadow-[0_4px_12px_rgba(37,99,235,0.2)]" style={{opacity: isSubmitting2FA ? '0.7' : '1'}}>
                                {isSubmitting2FA ? (
                                    <>
                                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Verifying...
                                    </>
                                ) : (
                                    "Verify Code"
                                )}
                            </button>
                            <div className="text-center mt-4">
                                <button type="button" onClick={() => { setRequires2FA(false); setTempToken(''); setLoginError(''); }} className="text-xs font-medium text-orange-500 hover:text-orange-600">Back to Login</button>
                            </div>
                        </form>
                    ) : (
                    <Formik
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                        onSubmit={validatUser}
                    >
                        {({ isSubmitting, handleChange }) => (
                            <Form className="space-y-2">
                                <div>
                                    <label className="block text-sm font-semibold text-slate-800 mb-1">Email Address</label>
                                    <div className="relative">
                                        <div className="absolute top-1/2 -translate-y-1/2 left-0 w-11 flex items-center justify-center pointer-events-none">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
                                        </div>
                                        <Field 
                                            autoFocus 
                                            type="text" 
                                            name="username" 
                                            placeholder="Enter your email" 
                                            className="w-full pl-11 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 hover:border-blue-500 transition-colors" 
                                            onChange={(e) => {
                                                handleChange(e);
                                                if (loginError) setLoginError('');
                                            }}
                                        />
                                    </div>
                                    <ErrorMessage name="username" component="div" className="text-red-500 text-xs mt-1"/>
                                </div>

                                <div>
                                    <div className="flex items-center justify-between mb-1">
                                        <label className="block text-sm font-semibold text-slate-800">Password</label>
                                        <button type="button" onClick={() => navigate('/forgot-password')} className="text-sm font-medium text-orange-500 hover:text-orange-600 transition-colors">Forgot password?</button>
                                    </div>
                                    <div className="relative">
                                        <div className="absolute top-1/2 -translate-y-1/2 left-0 w-11 flex items-center justify-center pointer-events-none">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                                        </div>
                                        <Field 
                                            type={showPassword ? "text" : "password"} 
                                            name="password" 
                                            placeholder="Enter your password" 
                                            className="w-full pl-11 pr-11 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 hover:border-blue-500 transition-colors" 
                                            onChange={(e) => {
                                                handleChange(e);
                                                if (loginError) setLoginError('');
                                            }}
                                        />
                                        <div className="absolute top-1/2 -translate-y-1/2 right-0 w-11 flex items-center justify-center cursor-pointer text-slate-400 hover:text-slate-600 transition-colors" onClick={() => setShowPassword(!showPassword)}>
                                            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                        </div>
                                    </div>
                                    <ErrorMessage name="password" component="div" className="text-red-500 text-xs mt-1"/>
                                </div>

                              {loginError && (
                                    <div className="px-3 py-2 rounded-md bg-red-50 text-red-600 text-xs font-medium flex items-start gap-1.5 border border-red-100">
                                        <svg
                                            className="flex-shrink-0 mt-0.5"
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="14"
                                            height="14"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <circle cx="12" cy="12" r="10" />
                                            <line x1="12" y1="8" x2="12" y2="12" />
                                            <line x1="12" y1="16" x2="12.01" y2="16" />
                                        </svg>

                                        <span className="min-w-0 break-words leading-4">
                                            {loginError}
                                        </span>
                                    </div>
                                )}

                                <div className="flex items-center justify-start gap-2">
                                    <input type="checkbox" id="remember" className="m-0 w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500" />
                                    <label htmlFor="remember" className="text-sm text-slate-500 cursor-pointer">Remember me</label>
                                </div>

                                <button type="submit" disabled={isSubmitting} className="w-full flex items-center justify-center gap-2 py-2.5 bg-[#2563eb] text-white rounded-xl hover:bg-blue-700 transition-colors font-semibold text-sm shadow-[0_4px_12px_rgba(37,99,235,0.2)]" style={{opacity: isSubmitting ? '0.7' : '1'}}>
                                    {isSubmitting ? (
                                        <svg className="animate-spin mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                    ) : (
                                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" x2="3" y1="12" y2="12"/></svg>
                                    )}
                                    {isSubmitting ? 'Signing in...' : 'Sign in'}
                                </button>

                                <div className="relative py-1.5 flex items-center">
                                    <div className="flex-grow border-t border-slate-200"></div>
                                    <span className="flex-shrink-0 mx-4 text-slate-400 text-xs font-medium">or</span>
                                    <div className="flex-grow border-t border-slate-200"></div>
                                </div>

                                <div className="text-center mt-4 text-sm text-slate-500">
                                    Don't have an account? <span className="text-orange-500 font-medium cursor-pointer hover:text-orange-600 transition-colors">Contact your administrator</span>
                                </div>
                            </Form>
                        )}
                    </Formik>
                    )}
                </div>
            </div>
            
            <div className="fixed bottom-2 right-8 text-xs text-slate-400 font-medium flex items-center gap-1.5 z-20">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                © {new Date().getFullYear()} Data Aces. All rights reserved.
            </div>

            <AuthIllustration />
        </div>
    )
};
export default Login;
